import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getHistory, deleteHistory, clearHistory } from "@/lib/api";
import { Trash, Image as ImageIcon } from "@phosphor-icons/react";

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function timeAgo(d) {
  const dt = new Date(d);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function History() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    setBusy(true);
    getHistory().then((data) => setItems(data || [])).finally(() => setBusy(false));
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;

  const remove = async (id) => {
    await deleteHistory(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all resize history?")) return;
    await clearHistory();
    setItems([]);
  };

  return (
    <div data-testid="history-page" className="max-w-7xl mx-auto px-6 md:px-10 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CCFF00]">Archive</span>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight mt-1">Resize history</h1>
          <p className="text-zinc-400 mt-2">A scrollable record of every image you've optimized.</p>
        </div>
        {items.length > 0 && (
          <button data-testid="clear-history" onClick={clearAll} className="btn-ghost">
            <Trash size={16} /> Clear all
          </button>
        )}
      </div>

      {busy ? (
        <div className="text-zinc-500">Loading history…</div>
      ) : items.length === 0 ? (
        <div className="surface p-12 text-center">
          <ImageIcon size={36} className="mx-auto text-zinc-600" />
          <div className="font-display text-2xl mt-4">Nothing here yet</div>
          <div className="text-zinc-500 mt-2">Your resized images will appear here automatically.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
          {items.map((it) => (
            <div key={it.id} data-testid={`history-item-${it.id}`} className="surface overflow-hidden group">
              <div className="checkerboard aspect-square flex items-center justify-center">
                {it.thumbnail ? (
                  <img src={it.thumbnail} alt={it.original_name} className="max-w-full max-h-full" />
                ) : (
                  <ImageIcon size={28} className="text-zinc-700" />
                )}
              </div>
              <div className="p-3">
                <div className="text-sm truncate">{it.original_name}</div>
                <div className="text-[11px] text-zinc-500 font-mono mt-1">{it.width}×{it.height} · {it.output_format.toUpperCase()} · {formatBytes(it.file_size)}</div>
                <div className="flex items-center justify-between mt-3">
                  <span className="chip text-[10px] capitalize">{it.platform}</span>
                  <span className="text-[10px] text-zinc-500 font-mono">{timeAgo(it.created_at)}</span>
                </div>
                <button
                  onClick={() => remove(it.id)}
                  data-testid={`history-delete-${it.id}`}
                  className="mt-3 text-[11px] text-zinc-500 hover:text-[#FF3366] transition flex items-center gap-1"
                >
                  <Trash size={12} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
